import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CATEGORIES, type Category, type Recipe } from '@ricettario/shared';
import { api } from '../api/client';
import { useRecipes } from '../hooks/useRecipes';
import RecipeCover from '../components/RecipeCover';

const GREETINGS = ['Ciao', 'Buongiorno', 'Buonasera'];
function greet() {
  const h = new Date().getHours();
  if (h < 12) return GREETINGS[1];
  if (h < 18) return GREETINGS[0];
  return GREETINGS[2];
}

function labelFor(cat: Category): string {
  return cat
    .toLowerCase()
    .split(' ')
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(' ');
}

export default function RecipeListPage() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<Category | null>(null);
  const { data, loading, error } = useRecipes(search, category);
  const recipes = useMemo(() => data ?? [], [data]);

  async function logout() {
    await api.post('/api/v1/auth/logout');
    window.location.href = '/login';
  }

  return (
    <main className="max-w-xl md:max-w-[1600px] mx-auto px-5 md:px-8 lg:px-12 pt-8 pb-32">
      <header className="flex items-start justify-between mb-5 animate-riseIn gap-4">
        <div className="min-w-0">
          <div className="text-sm text-muted font-medium">{greet()} 👋</div>
          <div className="font-script font-bold text-clay leading-none mt-1 text-[clamp(26px,7.2vw,34px)]">
            Ricettario Caluzzelli
          </div>
          <h1 className="mt-0.5 font-extrabold tracking-tightest leading-none text-[clamp(22px,6.8vw,30px)]">
            Cosa <span className="text-brand">cuciniamo</span> oggi?
          </h1>
        </div>
        <button onClick={logout} className="btn-ghost text-xs shrink-0">esci</button>
      </header>

      <div className="md:flex md:items-center md:gap-4 md:mb-5">
        <Link
          to="/menus"
          className="card p-4 flex items-center gap-3 mb-5 md:mb-0 md:w-[360px] md:shrink-0 no-tap active:scale-[0.99] transition animate-riseIn"
          style={{ animationDelay: '30ms' }}
        >
          <div
            className="h-11 w-11 rounded-2xl flex items-center justify-center text-xl shrink-0"
            style={{ background: 'linear-gradient(135deg,#FFD4C2,#F3856F)' }}
            aria-hidden
          >
            🛒
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[14px] leading-tight tracking-tightest">Menu settimanale</p>
            <p className="text-xs text-muted mt-0.5">pianifica la spesa e mandala a Bring</p>
          </div>
          <span className="text-muted text-lg shrink-0">→</span>
        </Link>

        <div className="relative mb-4 md:mb-0 md:flex-1 animate-riseIn" style={{ animationDelay: '60ms' }}>
          <span
            className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 bg-no-repeat bg-contain opacity-60"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%239F8F7F' stroke-width='2' stroke-linecap='round'><circle cx='11' cy='11' r='7'/><path d='m20 20-3.5-3.5'/></svg>\")",
            }}
            aria-hidden
          />
          <input
            type="search"
            className="input-bring pl-14"
            placeholder="cerca una ricetta, un ingrediente…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <nav
        className="flex flex-wrap gap-2 mb-5 animate-riseIn"
        style={{ animationDelay: '120ms' }}
        aria-label="categorie"
      >
        <Chip active={category === null} onClick={() => setCategory(null)}>
          Tutte · {recipes.length}
        </Chip>
        {CATEGORIES.map((c) => (
          <Chip
            key={c}
            active={category === c}
            onClick={() => setCategory(category === c ? null : c)}
          >
            {labelFor(c)}
          </Chip>
        ))}
      </nav>

      {loading && <ListSkeleton />}
      {error && (
        <p className="text-brand font-medium text-center mt-10">{error}</p>
      )}

      {!loading && !error && recipes.length === 0 && <EmptyState />}

      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
        {recipes.map((r, idx) => (
          <RecipeTile key={r.id} recipe={r} delay={Math.min(idx * 40, 400)} />
        ))}
      </div>

      <Link
        to="/recipe/new"
        className="btn-brand fixed bottom-5 right-5 h-14 pr-6 pl-5 no-tap"
        aria-label="nuova ricetta"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="h-5 w-5"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span>Nuova</span>
      </Link>
    </main>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition no-tap shadow-card ${
        active ? 'bg-ink text-white' : 'bg-card text-ink hover:bg-surface'
      }`}
    >
      {children}
    </button>
  );
}

function RecipeTile({ recipe, delay }: { recipe: Recipe; delay: number }) {
  const cat = recipe.category;
  return (
    <Link
      to={`/recipe/${recipe.id}`}
      className="card overflow-hidden flex flex-col no-tap active:scale-[0.98] transition animate-riseIn"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="aspect-square relative overflow-hidden">
        <RecipeCover recipe={recipe} size="lg" />
      </div>
      <div className="p-3.5">
        <h2 className="font-bold text-[15px] leading-[1.25] line-clamp-2 mb-1.5 tracking-tightest">
          {recipe.name}
        </h2>
        <div className="flex items-center gap-2 text-xs text-muted font-medium">
          {recipe.prepTime ? (
            <span className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" strokeLinecap="round" />
              </svg>
              {recipe.prepTime}′
            </span>
          ) : null}
          {cat && <CategoryChip category={cat} />}
        </div>
      </div>
    </Link>
  );
}

const CATEGORY_COLOR: Record<Category, string> = {
  ANTIPASTO: 'clay',
  PRIMO: 'brand',
  SECONDO: 'sky',
  CONTORNO: 'sage',
  'PIATTO UNICO': 'sun',
  DOLCE: 'lavender',
};

function CategoryChip({ category }: { category: Category }) {
  return (
    <span className={`chip chip-${CATEGORY_COLOR[category]} text-[10.5px] px-2 py-0.5`}>
      {labelFor(category)}
    </span>
  );
}

function ListSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
        <div key={i} className={`card overflow-hidden ${i > 3 ? 'hidden md:block' : ''}`}>
          <div className="skeleton aspect-square" />
          <div className="p-3.5 space-y-2">
            <div className="skeleton h-4 w-3/4" />
            <div className="skeleton h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="card py-14 text-center mt-4 animate-riseIn">
      <div
        className="mx-auto mb-5 h-20 w-20 rounded-3xl flex items-center justify-center text-5xl"
        style={{ background: 'linear-gradient(135deg,#FFD4C2,#F3856F)' }}
        aria-hidden
      >
        🍝
      </div>
      <p className="text-lg font-bold text-ink mb-1">Nessuna ricetta ancora.</p>
      <p className="text-sm text-muted px-10 mb-6">Comincia aggiungendo quella che oggi faresti ad occhi chiusi.</p>
      <Link to="/recipe/new" className="btn-brand">Crea la prima</Link>
    </div>
  );
}
